import React, {useCallback} from 'react';

import {BulkActionDownload, BulkActionResultModal} from 'src/components/bulk-tool';
import {CompleteMark} from 'src/components/complete-mark';
import {AccountLink, UserLink} from 'src/components/links';
import {RowNumbered} from 'src/components/row-numbered';
import {getAccountBulkChangeProgress, getAccountBulkChangeResult} from 'src/openapi/admin/apis/UserAccountBulkChangeApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';


import {FILE_HEADERS, FILE_NAME, TOOL_TITLE} from './constants';

import type {Pagination} from 'src/openapi/admin/models/Pagination';
import type {UserAccountProcessedItem} from 'src/openapi/admin/models/UserAccountProcessedItem';

type Props = {
    id: string,
    onClose: () => void,
};

const PLACE = `Bulk ${TOOL_TITLE} Result`;

export const UpdateUserAccountResultModal = ({id, onClose}: Props) => {
    const apiDispatch = useAppDispatch();

    const getPaginatedResults = useCallback(
        (bulkResult: UserAccountProcessedItem[], {currentPage, pageSize}: Pagination) => bulkResult
            .slice((currentPage - 1) * pageSize, currentPage * pageSize)
            .map((res: UserAccountProcessedItem, index) => (
                <Row key={index}>
                    <Cell width={50}>
                        <RowNumbered
                            maxRowNumber={bulkResult?.length || 0}
                            placeholder={(currentPage - 1) * pageSize + index + 1}
                        />
                    </Cell>
                    <Cell>
                        <UserLink userId={res.userId || 0} caption={res.userEmail} blank={true} place={PLACE}/>
                    </Cell>
                    <Cell>
                        <AccountLink
                            accountId={res.oldAccountId || 0}
                            accountName={res.oldAccountName}
                            blank={true}
                            place={PLACE}
                        />
                    </Cell>
                    <Cell>
                        <AccountLink
                            accountId={res.newAccountId || 0}
                            accountName={res.newAccountName}
                            blank={true}
                            place={PLACE}
                        />
                    </Cell>
                    <Cell>
                        <CompleteMark errors={res.errors}/>
                    </Cell>
                </Row>
            )),
        [],
    );

    return (
        <BulkActionResultModal<UserAccountProcessedItem>
            id={id}
            onClose={onClose}

            toolTitle={TOOL_TITLE}
            description={bulkResult => (
                <Group justify="space-between" alignItems="center">
                    <b>Update results</b>
                    <BulkActionDownload
                        toolName={TOOL_TITLE}
                        fileName={FILE_NAME}
                        headers={FILE_HEADERS}
                        startTime={bulkResult.startTime}
                        body={bulkResult.itemList?.map(item => ({
                            ...item,
                            userId: item.userId.toString(),
                            oldAccountId: item.oldAccountId.toString(),
                            newAccountId: item.newAccountId.toString(),
                            errors: item.errors?.join('. '),
                        })) as Record<string, string>[]}
                    />
                </Group>
            )}
            table={(bulkResult, pagination) => (
                <Table borderedGrid={true}>
                    <Head>
                        <Cell width={50}><strong>#</strong></Cell>
                        <Cell width={246}>User Email</Cell>
                        <Cell width={200}>Old Account</Cell>
                        <Cell width={200}>New Account</Cell>
                        <Cell width={150}>Action Status</Cell>
                    </Head>
                    <Body>
                        {getPaginatedResults(bulkResult, pagination)}
                    </Body>
                </Table>
            )}

            getProgress={() => apiDispatch(getAccountBulkChangeProgress({id}))}
            getResult={() => apiDispatch(getAccountBulkChangeResult({id}))}

            sortComparator={(a, b) => a.userEmail.localeCompare(b.userEmail)}
        />
    );
};
